import type { Project, RootScope } from '../types';
import type { ProjectSearchResult } from '../unit.search.panel/types';
import type { ObjectsNaviPanel as ObjectsNaviPanelType } from './objects.navi.panel.resource';

import { opportunityApi } from '../services/api';
import { isPublicMode } from '../services/publicMode';
import { getSharedWorkspaceContext } from '../services/sharedWorkspace';

objectsNaviPanelCtrl.$inject = [
  '$scope',
  '$rootScope',
  '$location',
  'ObjectsNaviPanel',
  '$timeout',
  '$translate',
];

interface ScopeProps {
  sectionId: string;
}

export interface objectsNaviPanelCtrl extends ScopeProps {
  isLoaded: boolean;
  publicMode: boolean;
  byHouseMode: boolean;
  objects: Project[] | ProjectSearchResult[];
  objectsOriginal: Project[] | ProjectSearchResult[];
  naviGroups: { label: string | null; objects: any[] }[];

  onSectionClick(section: { id: number }): void;
  resetFilter(): void;
}

export function objectsNaviPanelCtrl(
  this: objectsNaviPanelCtrl,
  $scope: angular.IScope,
  $rootScope: RootScope,
  $location: angular.ILocationService,
  ObjectsNaviPanel: ObjectsNaviPanelType,
  $timeout: angular.ITimeoutService,
  $translate: angular.translate.ITranslateService,
) {
  const vm = this;
  const sharedCtx = getSharedWorkspaceContext();

  // [{ house_id, section_ids[] }] set by sales manager, injected by PHP layout
  const selectedSegments = (window as any).sharedWorkspaceSelectedSegments as
    | { house_id: number; section_ids: number[] }[]
    | undefined;

  const swBase = (window as any).sharedWorkspaceBaseUrl as string | undefined;

  // house_id → Set of selected section IDs (null = no segments configured)
  const selectedMap: Map<number, Set<number>> | null = selectedSegments?.length
    ? new Map(
        selectedSegments.map(({ house_id, section_ids }) => [
          house_id,
          new Set(section_ids),
        ]),
      )
    : null;

  vm.isLoaded = false;
  vm.objects = [];
  vm.objectsOriginal = [];
  vm.naviGroups = [];
  vm.publicMode = isPublicMode();
  vm.byHouseMode = $location.absUrl().indexOf('/house') > 0 || !!sharedCtx;

  const request = {
    sectionId: vm.sectionId,
    ...(vm.publicMode ? { public: true } : {}),
    ...(vm.byHouseMode ? { byHouse: true } : {}),
  };

  const buildNaviGroups = (
    objects: any[],
  ): { label: string | null; objects: any[] }[] => {
    if (!selectedMap) {
      return [{ label: null, objects }];
    }

    const selectedObjects: any[] = [];
    const seeAlsoObjects: any[] = [];

    objects.forEach((obj: any) => {
      const selectedHouses: any[] = [];
      const seeAlsoHouses: any[] = [];

      (obj.houses ?? []).forEach((house: any) => {
        const sectionSet = selectedMap.get(house.id);

        if (sectionSet) {
          const selectedSections = (house.sectionList ?? []).filter((s: any) =>
            sectionSet.has(s.id),
          );

          if (selectedSections.length) {
            selectedHouses.push({ ...house, sectionList: selectedSections });
          }

          const seeAlsoSections = (house.sectionList ?? []).filter(
            (s: any) => !sectionSet.has(s.id),
          );

          if (seeAlsoSections.length) {
            seeAlsoHouses.push({ ...house, sectionList: seeAlsoSections });
          }
        } else {
          seeAlsoHouses.push(house);
        }
      });

      if (selectedHouses.length) {
        selectedObjects.push({ ...obj, houses: selectedHouses });
      }

      if (seeAlsoHouses.length) {
        seeAlsoObjects.push({ ...obj, houses: seeAlsoHouses });
      }
    });

    return [
      {
        label: $translate.instant('objects.navi.panel.group.selected'),
        objects: selectedObjects,
      },
      {
        label: $translate.instant('objects.navi.panel.group.see_also'),
        objects: seeAlsoObjects,
      },
    ];
  };

  const applySwLinks = (objects: any[]) => {
    if (!swBase) {
      return;
    }

    objects.forEach((object: any) => {
      object.houses?.forEach((house: any) => {
        house.link = `${swBase}/${house.id}`;
        house.sectionList?.forEach((section: any) => {
          section.link = `${swBase}/${house.id}#section${section.id}`;
        });
      });
    });
  };

  const initObjects = () => {
    if ($rootScope.filterObjects) {
      vm.objects = $rootScope.filterObjects.sort(
        (a, b) => Number(b.id) - Number(a.id),
      );
    } else {
      vm.objects = vm.objectsOriginal.slice();
    }

    applySwLinks(vm.objects);
    vm.naviGroups = buildNaviGroups(vm.objects);
  };

  ObjectsNaviPanel.getObjectNavi(request, function (resp) {
    vm.objectsOriginal = resp.data.sort((a, b) => Number(b.id) - Number(a.id));
    $rootScope.objectsNaviOriginal = vm.objectsOriginal;

    initObjects();

    vm.isLoaded = true;
  });

  $scope.$watch('$root.filterObjects', function () {
    initObjects();
  });

  vm.onSectionClick = (section: { id: number }) => {
    //only fires at sharedCtx
    if (sharedCtx?.opportunityId && sharedCtx?.urlToken) {
      opportunityApi
        .logSelectedSegmentAction({
          opportunityId: sharedCtx.opportunityId,
          urlToken: sharedCtx.urlToken,
          action: opportunityApi.ACTION.SECTION_CHANGE,
          sectionId: section.id,
        })
        .catch(() => {});
    }
  };

  vm.resetFilter = function () {
    // timeout so event happens not during $apply
    $timeout(function () {
      // call resetQuery() in unit.search.panel.controller.ts
      $rootScope.$broadcast('unit.search.panel:resetFilters');
    }, 100);
  };
}
